import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Question,
  WhatsappLogo,
  EnvelopeSimple,
  PlusCircle,
  PaperPlaneRight,
  Headset,
} from "@phosphor-icons/react";
import { api } from "@/lib/gado";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TICKET_STATUS = {
  aberto: { label: "Aberto", cls: "border-gado-gold/40 bg-gado-gold/10 text-gado-gold" },
  em_atendimento: { label: "Em atendimento", cls: "border-blue-500/30 bg-blue-500/10 text-blue-600" },
  resolvido: { label: "Resolvido", cls: "border-gado-green/30 bg-gado-green/10 text-gado-green" },
};

const fmtDT = (d) =>
  d ? new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

export default function Ajuda() {
  const qc = useQueryClient();
  const [newOpen, setNewOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [openTicket, setOpenTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: contact } = useQuery({
    queryKey: ["support-contact"],
    queryFn: async () => (await api.get("/support/contact")).data,
  });

  const { data: faqs = [], isLoading: loadingFaq } = useQuery({
    queryKey: ["faqs"],
    queryFn: async () => (await api.get("/faqs")).data,
  });

  const { data: tickets = [], isLoading: loadingTickets } = useQuery({
    queryKey: ["my-tickets"],
    queryFn: async () => (await api.get("/support/tickets")).data,
  });

  const refreshTickets = () => qc.invalidateQueries({ queryKey: ["my-tickets"] });

  const createTicket = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Informe assunto e mensagem");
      return;
    }
    setSaving(true);
    try {
      await api.post("/support/tickets", { subject: subject.trim(), message: message.trim() });
      toast.success("Chamado aberto — responderemos em breve");
      setNewOpen(false);
      setSubject("");
      setMessage("");
      refreshTickets();
    } catch {
      toast.error("Falha ao abrir chamado");
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!openTicket || !reply.trim()) return;
    setSaving(true);
    try {
      await api.post(`/support/tickets/${openTicket.ticket_id}/messages`, { message: reply.trim() });
      toast.success("Mensagem enviada");
      setReply("");
      setOpenTicket(null);
      refreshTickets();
    } catch {
      toast.error("Falha ao enviar");
    } finally {
      setSaving(false);
    }
  };

  const waLink = contact?.whatsapp
    ? `https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`
    : null;
  const mailLink = contact?.email ? `mailto:${contact.email}` : null;

  return (
    <div className="space-y-6" data-testid="ajuda-page">
      <div>
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Ajuda e Suporte</h1>
        <p className="text-gado-muted text-sm">Tire dúvidas, fale com a gente ou abra um chamado.</p>
      </div>

      {/* Contato direto */}
      {(waLink || mailLink) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ajuda-contato">
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ajuda-whatsapp"
              className="flex items-center gap-3 rounded-xl border border-gado-green/30 bg-gado-green/5 p-4 hover:border-gado-green/60 transition-colors"
            >
              <span className="h-10 w-10 rounded-xl bg-gado-green text-white grid place-items-center shrink-0">
                <WhatsappLogo size={20} weight="fill" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold text-gado-ink">Falar no WhatsApp</span>
                <span className="block text-xs text-gado-muted">Resposta rápida em horário comercial</span>
              </span>
            </a>
          )}
          {mailLink && (
            <a
              href={mailLink}
              data-testid="ajuda-email"
              className="flex items-center gap-3 rounded-xl border border-gado-line bg-white p-4 hover:border-gado-green/40 transition-colors"
            >
              <span className="h-10 w-10 rounded-xl bg-gado-gold/15 text-gado-gold grid place-items-center shrink-0">
                <EnvelopeSimple size={20} weight="fill" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold text-gado-ink">Enviar e-mail</span>
                <span className="block text-xs text-gado-muted truncate">{contact.email}</span>
              </span>
            </a>
          )}
        </div>
      )}

      {/* FAQ */}
      <section className="rounded-xl border border-gado-line bg-white p-4 md:p-5" data-testid="ajuda-faq">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-7 w-7 rounded-lg bg-gado-green/10 text-gado-green grid place-items-center">
            <Question size={15} weight="fill" />
          </span>
          <h2 className="font-cabinet font-bold text-gado-ink">Perguntas frequentes</h2>
        </div>
        {loadingFaq ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <div key={i} className="h-10 rounded-lg bg-gado-line/40 animate-pulse" />)}
          </div>
        ) : faqs.length === 0 ? (
          <p className="text-sm text-gado-muted" data-testid="ajuda-faq-empty">
            Nenhuma pergunta publicada ainda.
          </p>
        ) : (
          <Accordion type="single" collapsible>
            {faqs.map((f) => (
              <AccordionItem key={f.faq_id} value={f.faq_id} data-testid={`faq-${f.faq_id}`}>
                <AccordionTrigger className="text-sm font-semibold text-gado-ink text-left">
                  {f.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-gado-muted whitespace-pre-wrap">
                  {f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </section>

      {/* Meus chamados */}
      <section className="rounded-xl border border-gado-line bg-white p-4 md:p-5" data-testid="ajuda-tickets">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-gado-gold/15 text-gado-gold grid place-items-center">
              <Headset size={15} weight="fill" />
            </span>
            <h2 className="font-cabinet font-bold text-gado-ink">Meus chamados</h2>
          </div>
          <Button size="sm" onClick={() => setNewOpen(true)} data-testid="ajuda-novo-chamado"
            className="bg-gado-green hover:bg-gado-greenDark text-white">
            <PlusCircle size={15} className="mr-1.5" /> Abrir chamado
          </Button>
        </div>
        {loadingTickets ? (
          <div className="space-y-2">
            {[...Array(2)].map((_, i) => <div key={i} className="h-14 rounded-lg bg-gado-line/40 animate-pulse" />)}
          </div>
        ) : tickets.length === 0 ? (
          <p className="text-sm text-gado-muted" data-testid="ajuda-tickets-empty">
            Você ainda não abriu nenhum chamado.
          </p>
        ) : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <button
                key={t.ticket_id}
                onClick={() => { setOpenTicket(t); setReply(""); }}
                data-testid={`ticket-${t.ticket_id}`}
                className="w-full text-left rounded-lg border border-gado-line p-3.5 hover:border-gado-green/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-gado-ink truncate">{t.subject}</p>
                  <Badge variant="outline" className={`shrink-0 ${TICKET_STATUS[t.status]?.cls || ""}`}
                    data-testid={`ticket-status-${t.ticket_id}`}>
                    {TICKET_STATUS[t.status]?.label || t.status}
                  </Badge>
                </div>
                <p className="text-xs text-gado-muted mt-0.5">Atualizado {fmtDT(t.updated_at)}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Dialog: novo chamado */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="novo-chamado-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cabinet">Abrir chamado</DialogTitle>
            <DialogDescription>Descreva sua dúvida ou problema — a equipe responde por aqui.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Assunto (ex.: Dúvida sobre o plano Pro)"
              className="bg-white"
              data-testid="chamado-assunto-input"
            />
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Conte os detalhes…"
              rows={4}
              className="bg-white"
              data-testid="chamado-mensagem-input"
            />
          </div>
          <DialogFooter>
            <Button onClick={createTicket} disabled={saving} data-testid="chamado-enviar-button"
              className="w-full bg-gado-green hover:bg-gado-greenDark text-white">
              {saving ? "Enviando…" : "Abrir chamado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: conversa do chamado */}
      <Dialog open={!!openTicket} onOpenChange={(open) => !open && setOpenTicket(null)}>
        <DialogContent data-testid="chamado-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-cabinet">{openTicket?.subject}</DialogTitle>
            <DialogDescription>
              Status: {TICKET_STATUS[openTicket?.status]?.label || openTicket?.status}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-2 pr-1" data-testid="chamado-thread">
            {(openTicket?.messages || []).map((m, i) => (
              <div
                key={i}
                className={`rounded-lg p-3 text-sm max-w-[85%] ${
                  m.sender === "admin" ? "bg-gado-green text-white" : "ml-auto bg-gado-bg text-gado-ink"
                }`}
              >
                <p className="whitespace-pre-wrap">{m.message}</p>
                <p className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-white/60" : "text-gado-muted"}`}>
                  {m.sender === "admin" ? "Suporte" : "Você"} · {fmtDT(m.created_at)}
                </p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Escrever resposta…"
              className="bg-white"
              data-testid="chamado-reply-input"
              onKeyDown={(e) => { if (e.key === "Enter") sendReply(); }}
            />
            <Button onClick={sendReply} disabled={saving || !reply.trim()} data-testid="chamado-reply-send"
              className="bg-gado-green hover:bg-gado-greenDark text-white shrink-0" aria-label="Enviar resposta">
              <PaperPlaneRight size={16} />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
